import {isFeatureEnabled} from 'feature-flags';
import {Button} from 'platform/components';
import {Heading, HStack} from 'platform/foundation';

import {head, isEmpty, isNotNil} from 'ramda';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {COUNTRY_CZE, formatDocumentTitle} from '@dms/shared';

import {pushGtmEvent} from 'shared/web';

import {SourcingDataStatus} from '../../../components/SourcingDataStatus/SourcingDataStatus';
import {SourcingLayout} from '../../../components/SourcingLayout/SourcingLayout';
import {useSourcingTable} from '../../../components/SourcingTable/hooks/useSourcingTable';
import {useSourcingTableColumns} from '../../../components/SourcingTable/hooks/useSourcingTableColumns';
import {SourcingTable} from '../../../components/SourcingTable/SourcingTable';
import {SourcingUserListExportButton} from '../../../components/SourcingUserListExportButton/SourcingUserListExportButton';
import {VehiclePreview} from '../../../components/VehiclePreview/VehiclePreview';
import {
  CLASSIFIEDS_USER_LIST_ORDER_BY_MAP,
  CLASSIFIEDS_USER_LIST_SORT_OPTIONS,
} from '../../../constants/classifiedsSortBy';
import {DEFAULT_CURRENCY_CODE} from '../../../constants/DEFAULT_CURRENCY_CODE';
import {USER_LIST_DEFAULT_ORDER_ID} from '../../../constants/USER_LIST_DEFAULT_ORDER_ID';
import {useExportSourcingUserList} from '../../../hooks/useExportSourcingUserList';
import {usePaginatedUserList} from '../../../hooks/usePaginatedUserList';
import {useSortOrderByFromUrl} from '../../../hooks/useSortOrderByFromUrl';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingListAvailability} from '../../../hooks/useSourcingListAvailability';
import {useSourcingPricingStrategy} from '../../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType} from '../../../hooks/useSourcingType';
import {useSourcingUserListCount} from '../../../hooks/useSourcingUserListCount';
import {SourcingPage} from '../../../types/SourcingPage';
import {getSourcingUserListByPage} from '../../../utils/getSourcingUserListByPage';
import {getSourcingVehicleId} from '../../../utils/getSourcingVehicleId';
import {openCarvagoTransactionWorkspace} from '../../../utils/openCarvagoTransactionWorkspace';

/** Taken from the shared page mapping, so this page and its bulk action drawer export the same list. */
const USER_LIST = getSourcingUserListByPage(SourcingPage.CARVAGO_BUY_PAGE);

export function UserCarvagoBuyPage() {
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();
  const pricingStrategy = useSourcingPricingStrategy();
  const [state, dispatch] = useSourcingTable('user-carvago-buy', USER_LIST_DEFAULT_ORDER_ID);

  const {orderBy: sortOrderByFromUrl, clear: clearUrlSort} = useSortOrderByFromUrl();
  const orderBy = sortOrderByFromUrl ?? CLASSIFIEDS_USER_LIST_ORDER_BY_MAP.get(state.orderBy);

  const {
    queryResult: tableQueryResult,
    fulfilledTimeStamp,
    listQueryKey,
    isTerminalEmpty,
  } = usePaginatedUserList({
    query: {
      list: 'user-carvago-buy',
      orderBy: isEmpty(orderBy) ? undefined : orderBy?.join(','),
      currency: currency?.code ?? DEFAULT_CURRENCY_CODE,
      buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
      pricingStrategy,
    },
    sourcingType: SourcingType.CLASSIFIEDS,
  });

  const listCount = useSourcingUserListCount(USER_LIST);
  const {exportUserList, isExporting} = useExportSourcingUserList(USER_LIST);
  const handleExport = () => exportUserList({orderBy});

  const columns = useSourcingTableColumns();
  const rows = tableQueryResult.data;

  const classifiedsAdIds = rows.map((row) => row.sourcingVehicle.adId);
  useSourcingListAvailability({
    adIds: classifiedsAdIds,
    enabled: isFeatureEnabled(featureFlags.SOURCING_AVAILABILITY_CHECK),
    userLoadKey: fulfilledTimeStamp ?? 0,
  });

  const selectedAdId = state.selectedRow?.sourcingVehicle.adId;
  const preview = rows.find((row) => row.sourcingVehicle.adId === selectedAdId) ?? head(rows);

  return (
    <SourcingLayout
      hasPreview={isNotNil(preview) || tableQueryResult.isLoading}
      isPreviewLoading={tableQueryResult.isLoading && isEmpty(rows)}
      header={
        <HStack align="center" justify="space-between">
          <Heading size={2} alternative>
            {i18n.t('entity.vehicle.labels.carvagoBuyPage')}
          </Heading>
          <HStack spacing={2}>
            <SourcingUserListExportButton
              actionLabel={i18n.t('general.actions.exportAll')}
              vehiclesCount={listCount}
              isExporting={isExporting}
              onClick={handleExport}
              data-testid={testIds.sourcing.classifieds('carvagoBuy-exportButton')}
            />
            <Button
              title={i18n.t('entity.sourcing.actions.manageRequests')}
              onClick={() => {
                pushGtmEvent({
                  event: 'sourcing_classifieds_carvago_buy_opened',
                  event_id: 'sourcing_classifieds_carvago_buy_opened',
                });
                openCarvagoTransactionWorkspace();
              }}
              data-testid={testIds.sourcing.classifieds('carvagoBuy-manageRequestsButton')}
            />
          </HStack>
        </HStack>
      }
      content={
        <SourcingDataStatus
          isEmpty={isTerminalEmpty}
          iconUrl="/assets/images/empty_images/empty-basket.svg"
          text={i18n.t('entity.vehicle.notifications.nothingInCarvagoBuy')}
        >
          <SourcingTable
            state={state}
            dispatch={dispatch}
            columns={columns}
            sortByOptions={CLASSIFIEDS_USER_LIST_SORT_OPTIONS}
            queryResult={tableQueryResult}
            listQueryKey={listQueryKey}
            isSortByUnset={isNotNil(sortOrderByFromUrl)}
            onClearUrlSort={clearUrlSort}
            orderBy={orderBy}
          />
        </SourcingDataStatus>
      }
      iframe={
        <VehiclePreview
          key={getSourcingVehicleId(preview)}
          vehicleId={getSourcingVehicleId(preview)}
        />
      }
      pageTitle={formatDocumentTitle(
        i18n.t('entity.vehicle.labels.carvagoBuyPage'),
        i18n.t('page.sourcing.labels.classifieds'),
        i18n.t('page.sourcing.labels.sourcing')
      )}
    />
  );
}
